import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { GlobalScrollHelpers } from "@/components/GlobalScrollHelpers";

// Legacy redirect component for /humanoids/:id -> /robots/:id
const HumanoidRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/robots/${id}`} replace />;
};

// Eager load only the homepage
import Index from "./pages/Index";

// Lazy load all other pages
const Robots = lazy(() => import("./pages/Robots"));
const RobotProfile = lazy(() => import("./pages/RobotProfile"));
const AssetIntelligence = lazy(() => import("./pages/AssetIntelligence"));
const Compare = lazy(() => import("./pages/Compare"));
const Ecosystem = lazy(() => import("./pages/Ecosystem"));
const Companies = lazy(() => import("./pages/Companies"));
const CompanyProfile = lazy(() => import("./pages/CompanyProfile"));
const UseCases = lazy(() => import("./pages/UseCases"));
const UseCaseDetail = lazy(() => import("./pages/UseCaseDetail"));
const Technology = lazy(() => import("./pages/Technology"));
const TechnologyDetail = lazy(() => import("./pages/TechnologyDetail"));
const Opportunities = lazy(() => import("./pages/Opportunities"));
const OpportunityDetail = lazy(() => import("./pages/OpportunityDetail"));
const MarketSnapshot = lazy(() => import("./pages/MarketSnapshot"));
const MarketSnapshotDetail = lazy(() => import("./pages/MarketSnapshotDetail"));
const Insights = lazy(() => import("./pages/Insights"));
const Reports = lazy(() => import("./pages/Reports"));
const News = lazy(() => import("./pages/News"));
const Events = lazy(() => import("./pages/Events"));
const EventDetail = lazy(() => import("./pages/EventDetail"));
const Timeline = lazy(() => import("./pages/Timeline"));
const Recommend = lazy(() => import("./pages/Recommend"));
const Contribute = lazy(() => import("./pages/Contribute"));
const Newsletter = lazy(() => import("./pages/Newsletter"));
const Auth = lazy(() => import("./pages/Auth"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminRobots = lazy(() => import("./pages/admin/AdminRobots"));
const AdminCompanies = lazy(() => import("./pages/admin/AdminCompanies"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminSuggestions = lazy(() => import("./pages/admin/AdminSuggestions"));
const AdminNewsletter = lazy(() => import("./pages/admin/AdminNewsletter"));
const AdminFeatured = lazy(() => import("./pages/admin/AdminFeatured"));
const AdminOpportunities = lazy(() => import("./pages/admin/AdminOpportunities"));
const AdminEvents = lazy(() => import("./pages/admin/AdminEvents"));
const AdminNewsSources = lazy(() => import("./pages/admin/AdminNewsSources"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews"));
const AdminListings = lazy(() => import("./pages/admin/AdminListings"));
const AdminBookings = lazy(() => import("./pages/admin/AdminBookings"));
const AdminProfiles = lazy(() => import("./pages/admin/AdminProfiles"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Marketplace pages
const MarketplaceHome = lazy(() => import("./pages/marketplace/MarketplaceHome"));
const SkillMarketplace = lazy(() => import("./pages/marketplace/SkillMarketplace"));
const SkillDetail = lazy(() => import("./pages/marketplace/SkillDetail"));
const Rentals = lazy(() => import("./pages/marketplace/Rentals"));
const MarketplaceJoin = lazy(() => import("./pages/marketplace/MarketplaceJoin"));
const ListingCreate = lazy(() => import("./pages/marketplace/ListingCreate"));
const ListingEdit = lazy(() => import("./pages/marketplace/ListingEdit"));
const ListingDetail = lazy(() => import("./pages/marketplace/ListingDetail"));
const MarketplaceDashboard = lazy(() => import("./pages/marketplace/MarketplaceDashboard"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
  </div>
);

// Optimized QueryClient configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <GlobalScrollHelpers />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Core */}
            <Route path="/" element={<Index />} />
            <Route path="/robots" element={<Robots />} />
            <Route path="/robots/:slug" element={<RobotProfile />} />
            <Route path="/robots/:slug/intelligence" element={<AssetIntelligence />} />
            <Route path="/companies" element={<Companies />} />
            <Route path="/companies/:slug" element={<CompanyProfile />} />
            
            {/* Intelligence Layer */}
            <Route path="/ecosystem" element={<Ecosystem />} />
            <Route path="/timeline" element={<Timeline />} />
            <Route path="/use-cases" element={<UseCases />} />
            <Route path="/use-cases/:id" element={<UseCaseDetail />} />
            <Route path="/technology" element={<Technology />} />
            <Route path="/technology/:id" element={<TechnologyDetail />} />
            
            {/* Opportunity Layer */}
            <Route path="/opportunities" element={<Opportunities />} />
            <Route path="/opportunities/:id" element={<OpportunityDetail />} />
            <Route path="/compare" element={<Compare />} />
            <Route path="/recommend" element={<Recommend />} />
            
            {/* Insights */}
            <Route path="/market-snapshot" element={<MarketSnapshot />} />
            <Route path="/market-snapshot/:slug" element={<MarketSnapshotDetail />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/news" element={<News />} />
            <Route path="/events" element={<Events />} />
            <Route path="/events/:id" element={<EventDetail />} />
            
            {/* Community */}
            <Route path="/contribute" element={<Contribute />} />
            <Route path="/newsletter" element={<Newsletter />} />
            
            {/* Marketplace */}
            <Route path="/marketplace" element={<MarketplaceHome />} />
            <Route path="/marketplace/skills" element={<SkillMarketplace />} />
            <Route path="/marketplace/skills/:slug" element={<SkillDetail />} />
            <Route path="/marketplace/rentals" element={<Rentals />} />
            <Route path="/marketplace/rentals/:id" element={<ListingDetail />} />
            <Route path="/marketplace/join" element={<MarketplaceJoin />} />
            <Route path="/marketplace/listings/new" element={<ListingCreate />} />
            <Route path="/marketplace/listings/:id/edit" element={<ListingEdit />} />
            <Route path="/marketplace/dashboard" element={<MarketplaceDashboard />} />
            
            {/* Legacy redirects */}
            <Route path="/catalog" element={<Navigate to="/robots" replace />} />
            <Route path="/humanoids/:id" element={<HumanoidRedirect />} />
            
            {/* Auth & Admin */}
            <Route path="/auth" element={<Auth />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/robots" element={<AdminRobots />} />
            <Route path="/admin/companies" element={<AdminCompanies />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/suggestions" element={<AdminSuggestions />} />
            <Route path="/admin/newsletter" element={<AdminNewsletter />} />
            <Route path="/admin/featured" element={<AdminFeatured />} />
            <Route path="/admin/opportunities" element={<AdminOpportunities />} />
            <Route path="/admin/events" element={<AdminEvents />} />
            <Route path="/admin/news-sources" element={<AdminNewsSources />} />
            <Route path="/admin/reviews" element={<AdminReviews />} />
            <Route path="/admin/listings" element={<AdminListings />} />
            <Route path="/admin/bookings" element={<AdminBookings />} />
            <Route path="/admin/profiles" element={<AdminProfiles />} />
            
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
